import {
  Dropdown,
  leadsImportColumnUniqueValuesRetrieveOptions,
  leadsImportListQueryKey,
  leadsImportPartialUpdateMutation,
  leadsImportPreviewRetrieveOptions,
  leadsImportRetrieveOptions,
  LeadsStatusesListData,
  leadsStatusesListListInfiniteOptions,
  LeadsTypesListData,
  leadsTypesListListInfiniteOptions,
  PaginatedDropdownList,
  StepEnum
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { useImportLeads } from '@crm/features/leads/contexts'
import { useStyles } from '@crm/features/leads/styles'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useIdParam } from '@crm/hooks/useIdParam'
import { paths } from '@crm/routes'
import { defaultFiltersMultipleValues } from '@crm/utils'
import { Text } from '@shared/components'
import { useInvalidatePredicateQueries, useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, App, Card, Col, Flex, Form, List, Row, Select } from 'antd'
import { DefaultOptionType } from 'antd/es/select'
import { useEffect, useMemo } from 'react'

export const PipelineStatus = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const { id } = useIdParam()
  const { params, updateQuery } = useQueryUpdate()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const navigate = useAppNavigate()
  const { setIsPending } = useImportLeads()

  const leadTypeValue = defaultFiltersMultipleValues(params?.lead_type, true)
  const selectedLeadTypeId = Form.useWatch('lead_type', form)
  const selectedFileField = Form.useWatch('status_column', form)

  const { data: leadImport, isLoading: isLoadingImport } = useQuery({
    ...leadsImportRetrieveOptions({ path: { id: id } }),
    enabled: !!id,
    refetchOnMount: 'always'
  })

  const { data: preview } = useQuery({
    ...leadsImportPreviewRetrieveOptions({ path: { id: id } }),
    enabled: !!id
  })

  const { data: statusUniqueValues, isLoading } = useQuery({
    ...leadsImportColumnUniqueValuesRetrieveOptions({
      path: { id: id },
      query: { column: Number(selectedFileField!) }
    }),
    enabled: selectedFileField != null
  })

  useEffect(() => {
    if (!leadImport && !isLoadingImport) {
      message.error(t('import.error_load_import_data'))

      return
    }

    form.setFieldsValue({
      lead_type: leadImport?.lead_type,
      status_column: leadImport?.status_column,
      status_mapping: leadImport?.status_mapping || {}
    })
  }, [leadImport, isLoadingImport, message, t])

  const fileFieldsOption: DefaultOptionType[] = useMemo(
    () =>
      (Array.isArray(preview?.data?.[0]) ? preview.data[0] : []).map(
        (item: string, index: number) => ({
          label: item,
          value: index
        })
      ),
    [preview]
  )

  const { mutate: updateMutation } = useMutation({
    ...leadsImportPartialUpdateMutation(),
    onMutate: () => setIsPending(true),
    onSuccess: (response) => {
      invalidateQueries(leadsImportListQueryKey())
      navigate(
        paths.leadImport.item(
          response.id.toString(),
          response.step?.toString() || ''
        )
      )
    },
    onError: (error) => showApiErrors(error, form),
    onSettled: () => setIsPending(false)
  })

  const onSubmit = () =>
    updateMutation({
      path: { id: id },
      body: {
        ...form.getFieldsValue(),
        step: StepEnum.PIPELINE_STATUS_MAPPING
      }
    })

  return (
    <>
      <Text weight={500} size={18}>
        {t('import.pipeline_status_mapping_description')}
      </Text>

      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        name={`form-${StepEnum.PIPELINE_STATUS_MAPPING}`}
        className="mt-20"
      >
        <Card>
          <Row gutter={16} className="w-full">
            <Col span={12}>
              <Form.Item
                className={styles.selectWidth}
                name="status_column"
                label={t('import.select_file_field')}
              >
                <Select
                  className="select-min-width"
                  options={fileFieldsOption}
                  placeholder={t('main.select')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lead_type"
                label={t('import.select_pipeline')}
                className={styles.selectWidth}
              >
                <InfiniteSelect<
                  LeadsTypesListData,
                  PaginatedDropdownList,
                  Dropdown
                >
                  className="select-min-width"
                  placeholder={t('contacts.pipeline')}
                  getQueryFunction={leadsTypesListListInfiniteOptions}
                  onChange={(leadTypeId) => {
                    updateQuery(leadTypeId)
                  }}
                  baseOptions={{ query: { preference: params?.lead_type } }}
                  value={leadTypeValue}
                />
              </Form.Item>
            </Col>
          </Row>

          <List
            header={
              <Flex justify="space-between">
                <Text className={styles.selectWidth} weight={600}>
                  {t('import.file_values')}
                </Text>
                <Text className={styles.selectWidth} weight={600}>
                  {t('import.pipeline_statuses')}
                </Text>
              </Flex>
            }
            dataSource={statusUniqueValues}
            loading={isLoading}
            renderItem={(fileValue) => (
              <List.Item>
                <Row gutter={16} className="w-full">
                  <Col span={12}>
                    <Text>{fileValue}</Text>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['status_mapping', fileValue]} noStyle>
                      <InfiniteSelect<
                        LeadsStatusesListData,
                        PaginatedDropdownList,
                        Dropdown
                      >
                        placeholder={t('filter.status')}
                        className="select-min-width"
                        getQueryFunction={leadsStatusesListListInfiniteOptions}
                        baseOptions={{
                          query: {
                            lead_type: selectedLeadTypeId
                              ? Number(selectedLeadTypeId)
                              : undefined
                          }
                        }}
                        disabled={!selectedLeadTypeId}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </List.Item>
            )}
          />
        </Card>

        <Alert
          message={t('import.rows_without_status_receive_default')}
          type="warning"
          className="mt-20"
        />
      </Form>
    </>
  )
}
